import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  List,
  ListItem,
  Text,
  Portal,
  useOutsideClick,
  useColorModeValue,
  Spinner,
  Icon
} from '@chakra-ui/react';
import { SearchIcon, CloseIcon, ChevronDownIcon } from '@chakra-ui/icons';

const SearchableSelectChakra = ({
  options = [],
  value,
  onChange,
  placeholder = "Selecione...",
  displayField,
  valueField = '_id',
  isLoading = false,
  isDisabled = false,
  renderOption,
  size = 'md',
  width = '100%',
  maxDropdownHeight = '300px'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const selectedBg = useColorModeValue('green.50', 'green.900');
  const selectedColor = useColorModeValue('green.600', 'green.200');

  // Close dropdown when clicking outside
  useOutsideClick({
    ref: containerRef,
    handler: () => {
      setIsOpen(false);
      setSearchTerm('');
    },
  });

  // Get display text for an option
  const getDisplayText = (option) => {
    if (!option) return '';
    
    if (typeof displayField === 'function') {
      return displayField(option);
    }
    
    if (typeof displayField === 'string') {
      return option[displayField];
    }
    
    // Default fields
    return option.nomeCompleto || option.name || option.title || option.label || '';
  };

  // Get selected option
  const selectedOption = options.find(opt => opt[valueField] === value);
  const displayValue = selectedOption ? getDisplayText(selectedOption) : '';

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => {
        const text = getDisplayText(option).toLowerCase();
        const search = searchTerm.toLowerCase();
        return text.includes(search);
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, displayField]);

  const handleSelect = (option) => {
    onChange(option[valueField]);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  const handleInputClick = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  };

  const inputSizes = {
    sm: 'sm',
    md: 'md',
    lg: 'lg'
  };

  return (
    <Box position="relative" ref={containerRef} width={width}>
      <InputGroup 
        size={inputSizes[size]} 
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        onClick={handleInputClick}
      >
        {!isOpen && <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>}
        
        <Input
          ref={inputRef}
          value={isOpen ? searchTerm : displayValue}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={isOpen ? "Digite para buscar..." : placeholder}
          isDisabled={isDisabled}
          readOnly={!isOpen}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          pr="4rem"
        />
        
        <InputRightElement width="4rem">
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            <>
              {value && !isOpen && (
                <IconButton
                  size="xs"
                  icon={<CloseIcon />}
                  variant="ghost"
                  onClick={handleClear}
                  isDisabled={isDisabled}
                  mr={1}
                  aria-label="Limpar seleção"
                />
              )}
              <Icon 
                as={ChevronDownIcon} 
                transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                transition="transform 0.2s"
                color="gray.400"
              />
            </>
          )}
        </InputRightElement>
      </InputGroup>

      {isOpen && !isDisabled && (
        <Box
          position="absolute"
          top="100%"
          left="0"
          right="0"
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          mt={1}
          boxShadow="lg"
          zIndex={1500}
          maxH={maxDropdownHeight}
          overflowY="auto"
          sx={{
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'gray.100',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'gray.400',
              borderRadius: '3px',
            },
          }}
          >
            {filteredOptions.length === 0 ? (
              <Box p={4} textAlign="center" color="gray.500">
                <Text fontSize="sm">Nenhuma opção encontrada</Text>
              </Box>
            ) : (
              <List spacing={0}>
                {filteredOptions.map((option) => {
                  const isSelected = option[valueField] === value;
                  return (
                    <ListItem
                      key={option[valueField]}
                      px={3}
                      py={2}
                      cursor="pointer"
                      bg={isSelected ? selectedBg : 'transparent'}
                      color={isSelected ? selectedColor : 'inherit'}
                      _hover={{ bg: isSelected ? selectedBg : hoverBg }}
                      onClick={() => handleSelect(option)}
                      transition="all 0.2s"
                    >
                      {renderOption ? (
                        renderOption(option, isSelected)
                      ) : (
                        <Text fontSize={size === 'sm' ? 'sm' : 'md'} fontWeight={isSelected ? 'medium' : 'normal'}>
                          {getDisplayText(option)}
                        </Text>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
      )}
    </Box>
  );
};

export default SearchableSelectChakra;